import { router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { SegmentedControl } from "@/components/segmented-control"
import { brandAnalyticsCrewPath } from "@/routes"
import { type CrewAnalyticsWindow, WINDOW_VALUES } from "@/types/crew-analytics"

/**
 * OKS-65 — Window toggle for the crew-wide analytics surface.
 *
 * Toggle change → partial Inertia reload re-fetching the window echo
 * prop (`window`) plus the window-driven data props (`kpis_window`,
 * `top_performers`, `engagement`). The window-INVARIANT props
 * (`kpis_snapshot`, `at_risk`) stay mounted with their current values
 * — PRD §5 + TRD K8 + K12 lock.
 *
 * OKS-635: `window` is included in the `only:` set (echo-first) so the
 * server re-echoes the newly-selected window on the partial reload and
 * the controlled `value` re-checks the active pill. Without it the pill
 * lagged on a stale echo (mirrors the EMV `range` reference). Note
 * `window` is variant (it changes with the selection) — the earlier
 * "window-invariant" framing was the bug.
 *
 * URL query is the source of truth; toggle is controlled via the
 * `value` prop (caller threads the URL-echoed `window` prop from the
 * page).
 */
interface CrewAnalyticsWindowToggleProps {
  value: CrewAnalyticsWindow
}

const RELOAD_ONLY = ["window", "kpis_window", "top_performers", "engagement"]

export function CrewAnalyticsWindowToggle({
  value,
}: CrewAnalyticsWindowToggleProps) {
  const { t } = useTranslation()

  const handleChange = (next: CrewAnalyticsWindow) => {
    // Radio-group never re-fires for the already-active option, so no
    // empty-string / no-op guard is needed (unlike the old ToggleGroup).
    router.get(
      brandAnalyticsCrewPath({ window: next }),
      {},
      {
        only: RELOAD_ONLY,
        preserveScroll: true,
        preserveState: false,
      },
    )
  }

  return (
    <SegmentedControl
      value={value}
      onValueChange={handleChange}
      data-testid="crew-analytics-window-toggle"
      aria-label={t("analytics.crew.window.label")}
      className="flex-wrap"
      options={WINDOW_VALUES.map((window) => ({
        value: window,
        label: t(`analytics.crew.window.${window}`),
        "data-testid": `crew-analytics-window-toggle-${window}`,
        "aria-label": t(`analytics.crew.window.${window}`),
      }))}
    />
  )
}
